/**
 * Exit Demo — leave the isolated Demo Company cleanly.
 *
 * Demo access is NOT a shared demo login and NOT a temporary session: the user
 * signs up as themselves and is given an additional `company_members` row in
 * the isolated demo tenant (`company_settings.is_demo`, `demo_tenants`), joined
 * through the `join_demo_company` RPC. The active tenant is remembered in
 * `localStorage.selected_company_id`, and `ProtectedRoute` skips the plan
 * redirect only while that selected company really is the demo tenant.
 *
 * Exiting therefore has to undo all three layers, in this order:
 *   1. the membership   — `leave_demo_company` RPC, so RLS stops returning demo
 *                         rows for this user on every device and every tab;
 *   2. the client state — selected company + every tenant-scoped cache;
 *   3. the auth session — always terminated, so a refresh, a new tab or browser
 *                         Back cannot restore authenticated demo access. The
 *                         user lands on the public sign-in page, signed in as
 *                         nobody — never as another account.
 *
 * Normal sign-out, subscriptions, Stripe, membership logic for real companies
 * and RLS policies are untouched.
 */
import { supabase } from "@/integrations/supabase/client";
import { leaveDemoCompany } from "@/services/demoTenant";
import { purgeTenantCaches } from "@/lib/tenantCacheGuard";
import { isSuperAdminEmail } from "@/hooks/useSuperAdminAccess";

export interface ExitDemoOutcome {
  kind: "signed_out" | "not_applicable";
  redirectTo: string;
}

export async function exitDemo(
  demoCompanyId: string,
  opts: { markManualLogout?: () => void } = {},
): Promise<ExitDemoOutcome> {
  // 0. Safety: Exit Demo can never sign out a Super Admin. A Super Admin is
  //    not a demo user, so there is no demo session to terminate.
  const { data: authData } = await supabase.auth.getUser();
  if (isSuperAdminEmail(authData?.user?.email)) {
    return { kind: "not_applicable", redirectTo: "/sa" };
  }

  // 1. Membership — must succeed, otherwise demo data would still be readable.
  await leaveDemoCompany(demoCompanyId);

  // 2. Client state — clears React Query, the jobs cache, snapshots, drafts and
  //    the selected company key. Queued uploads are dropped as well: anything
  //    queued inside the demo can never belong to a real tenant.
  await purgeTenantCaches({ clearQueue: true });

  // 3. Auth session — terminated exactly as a normal sign-out does, using the
  //    same suppression hook so no "session expired" banner appears.
  opts.markManualLogout?.();
  await supabase.auth.signOut();

  return { kind: "signed_out", redirectTo: "/auth?exited=demo" };
}
